import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Form, Select } from 'formik-antd';
import { Col, Row, message, Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import styled from 'styled-components';
import { FormInput, CustomInput } from 'atoms';
import { searchClientFiltersV2, searchPincodeV2, getClientByIdV2 } from 'store/clientSliceV2';
import { searchBusinessUnitsV2 } from 'store/projectSliceV2';
import AddressPickerV2 from 'components/common/AddressPickerV2/AddressPickerV2';
import loadGoogleMapsV2 from 'utils/loadGoogleMapsV2';
import { truncateAddressToCity } from 'utils/addressHelpers';
import {
	PROJECT_TYPE_OPTIONS,
	PROJECT_TYPE_DESCRIPTIONS,
	PROJECT_TYPE_TRANSPORTATION,
	isWarehousingProject,
} from './projectTypes';
import {
	FormContainer,
	DisabledInput,
	NextButton,
	SaveButton,
	CancelButton,
} from '../client/clientFormStyles';

// Project Type is presented as a full-width callout rather than a plain form field:
// the choice reshapes the rest of the wizard, so the explanatory copy sits next to it.
const ProjectTypePanel = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
	padding: 20px 24px;
	margin-bottom: 24px;
	background: #fafafa;
	border: 1px solid #e8e8e8;
	border-radius: 8px;

	@media (max-width: 767px) {
		flex-direction: column;
		align-items: stretch;
		gap: 12px;
	}
`;

const ProjectTypeCopy = styled.div`
	flex: 1;
	min-width: 0;
`;

const ProjectTypeLabel = styled.div`
	font-size: 16px;
	line-height: 24px;
	font-weight: 600;
	color: #262626;

	&::after {
		content: '*';
		margin-left: 4px;
		color: #ff4d4f;
	}
`;

const ProjectTypeHint = styled.div`
	margin-top: 4px;
	font-size: 14px;
	line-height: 22px;
	color: #8c8c8c;
`;

const ProjectTypeControl = styled.div`
	flex-shrink: 0;
	width: 260px;

	.ant-form-item {
		margin-bottom: 0;
	}

	@media (max-width: 767px) {
		width: 100%;
	}
`;

// POD Required Options
const POD_REQUIRED_OPTIONS = [
	{ value: true, label: 'Yes' },
	{ value: false, label: 'No' },
];

const apporvalTypesOptions = [
	{ value: 'true', label: 'Yes' },
	{ value: 'false', label: 'No' },
];

export default function ProjectInformationFormV2({
	projectInfoData,
	setProjectInfoData,
	setCurrentStep,
	isEditMode,
	isViewMode,
	onSubmit, // eslint-disable-line no-unused-vars
	onCancel,
	onSave,
	loading,
	savedProjectId,
	onProjectTypeChange,
}) {
	const dispatch = useDispatch();
	const [clientsData, setClientsData] = useState([]);
	const [businessUnitsData, setBusinessUnitsData] = useState([]);
	const [loadingClients, setLoadingClients] = useState(false);
	const [loadingBusinessUnits, setLoadingBusinessUnits] = useState(false);
	const [selectedClientPan, setSelectedClientPan] = useState(null);
	const [gstValidationError, setGstValidationError] = useState('');
	// Mirrored outside Formik so the footer buttons (rendered outside the render prop)
	// can branch on it. Kept in sync by the Project Type Select's onChange.
	const [projectType, setProjectType] = useState(
		projectInfoData?.project_type || PROJECT_TYPE_TRANSPORTATION
	);

	// Revenue model is only writable at creation, so the type is locked once saved.
	const canEditProjectType = !isEditMode;
	// Warehousing is created in one call from step 2 (the revenue model is part of the
	// create payload), so step 1 does not save on its own in create mode.
	const deferCreateToStep2 = !isEditMode && isWarehousingProject(projectType);

	// Create validation schema with dynamic GST validation
	const ProjectInfoSchema = useMemo(
		() =>
			Yup.object().shape({
				project_name: Yup.string()
					.required('Project name is required')
					.min(3, 'Project name must be at least 3 characters')
					.max(256, 'Project name must not exceed 256 characters'),
				project_address: Yup.string()
					.required('Project address is required')
					.min(5, 'Project address must be at least 5 characters'),
				pincode: Yup.string()
					.required('Pincode is required')
					.matches(/^\d{6}$/, 'Pincode must be exactly 6 digits'),
				client_id: Yup.mixed().test(
					'client-id-required',
					'Client selection is required',
					(value) => {
						if (value === undefined || value === null) return false;
						if (typeof value === 'string') return value.trim() !== '';
						return true;
					}
				),
				latitude: Yup.string()
					.required('Latitude is required')
					.max(50, 'Latitude must not exceed 50 characters')
					.matches(/^[\d°.-\s]+$/, 'Invalid latitude format'),
				longitude: Yup.string()
					.required('Longitude is required')
					.max(50, 'Longitude must not exceed 50 characters')
					.matches(/^[\d°.-\s]+$/, 'Invalid longitude format'),
				business_unit_id: Yup.number()
					.required('Business unit is required')
					.typeError('Business unit is required'),
				// Warehousing projects run no trips, so POD and adhoc-trip approval do
				// not apply — they stay on the form but are optional for that type.
				pod_required: Yup.boolean().when('project_type', (type, schema) =>
					isWarehousingProject(type)
						? schema.notRequired()
						: schema.required('POD Required selection is required')
				),
				adhoc_approval_required: Yup.string().when('project_type', (type, schema) =>
					isWarehousingProject(type)
						? schema.notRequired()
						: schema.required('Adhoc approval selection is required')
				),
				project_type: Yup.string().required('Project type is required'),
				gst_number: Yup.string()
					.transform((value) => (value === '' ? undefined : value))
					.notRequired()
					.length(15, 'GST number must be exactly 15 characters')
					.matches(
						/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
						'Invalid GST number format'
					)
					.test(
						'gst-pan-match',
						'GST number must match client PAN',
						function validateGstPan(value) {
							if (!value || !selectedClientPan) return true;
							// Extract PAN from GST (positions 3-12 in 1-indexed, or indices 2-12 in substring)
							const gstPan = value.substring(2, 12);
							return gstPan === selectedClientPan;
						}
					),
			}),
		[selectedClientPan]
	);

	// Helper functions for initial values (moved outside to reduce complexity)
	const getPodRequired = (data) =>
		data?.pod_required !== undefined ? data.pod_required : undefined;

	const getPincode = (data) => data?.pincode || data?.pin_code || '';

	// Initial Values
	const initialValues = useMemo(() => {
		const data = projectInfoData || {};
		// Truncate address to city if it exists (for existing data from backend)
		const projectAddress = data.project_address
			? truncateAddressToCity(data.project_address, data.city)
			: '';
		return {
			project_name: data.project_name || '',
			project_address: projectAddress,
			pincode: getPincode(data),
			zone: data.zone || '',
			city: data.city || '',
			state: data.state || '',
			client_id: data.client_id || undefined,
			latitude: data.latitude || '',
			longitude: data.longitude || '',
			business_unit_id: data.business_unit_id || undefined,
			pod_required: getPodRequired(data),
			gst_number: data.gst_number || '',
			adhoc_approval_required:
				data.adhoc_approval_required !== undefined
					? String(data.adhoc_approval_required)
					: undefined,
			project_type: data.project_type || PROJECT_TYPE_TRANSPORTATION,
		};
	}, [projectInfoData]);

	// Load Google Maps on component mount
	useEffect(() => {
		loadGoogleMapsV2().catch((err) => {
			console.error('Failed to load Google Maps:', err);
			message.error('Failed to load Google Maps. Please refresh the page.');
		});
	}, []);

	// Fetch clients list on mount
	useEffect(() => {
		setLoadingClients(true);
		dispatch(
			searchClientFiltersV2({
				filter_by: { is_active: true },
				limit: 1000,
			})
		)
			.then((response) => {
				setClientsData(response?.results || []);
			})
			.catch(() => {
				// Error handled silently or show user-friendly message
			})
			.finally(() => {
				setLoadingClients(false);
			});
	}, [dispatch]);

	// Fetch business units on mount
	useEffect(() => {
		setLoadingBusinessUnits(true);
		dispatch(
			searchBusinessUnitsV2({
				filter_by: {},
				limit: 1000,
			})
		)
			.then((response) => {
				setBusinessUnitsData(response?.results || []);
			})
			.catch(() => {
				// Error handled silently or show user-friendly message
			})
			.finally(() => {
				setLoadingBusinessUnits(false);
			});
	}, [dispatch]);

	// Keep the local mirror aligned when the parent reloads project data
	useEffect(() => {
		setProjectType(projectInfoData?.project_type || PROJECT_TYPE_TRANSPORTATION);
	}, [projectInfoData?.project_type]);

	// Extract PAN from client data
	const extractClientPan = (clientData) => {
		const panDoc = clientData.business_documents?.find((doc) => doc.document_type === 'PAN');
		return panDoc?.pan_number || clientData.pan_number;
	};

	// Validate GST against PAN
	const validateGstAgainstPan = (gstNumber, panNumber) => {
		if (!gstNumber || gstNumber.length < 12) return;
		const gstPan = gstNumber.substring(2, 12);
		if (gstPan !== panNumber) {
			setGstValidationError('GST number must match client PAN number');
		} else {
			setGstValidationError('');
		}
	};

	// Fetch client PAN when client is selected
	const handleClientChange = async (clientId, setFieldValue, currentGstNumber) => {
		if (!clientId) {
			setSelectedClientPan(null);
			setGstValidationError('');
			setFieldValue('adhoc_approval_required', undefined);
			return;
		}

		try {
			const clientData = await dispatch(getClientByIdV2({ id: clientId }));
			if (!clientData) return;

			if (clientData.adhoc_approval_required !== undefined) {
				setFieldValue(
					'adhoc_approval_required',
					String(clientData.adhoc_approval_required)
				);
			}

			const panNumber = extractClientPan(clientData);
			if (panNumber) {
				setSelectedClientPan(panNumber);
				validateGstAgainstPan(currentGstNumber, panNumber);
			} else {
				setSelectedClientPan(null);
				setGstValidationError('Client does not have a PAN number');
			}
		} catch (error) {
			setSelectedClientPan(null);
		}
	};

	// Handle pincode lookup - Auto-fetch zone, city, state when user types 6-digit pincode
	const handlePincodeLookup = async (pincode, setFieldValue) => {
		if (pincode && pincode.length === 6 && /^\d{6}$/.test(pincode)) {
			try {
				const result = await dispatch(searchPincodeV2({ pincode }));
				if (result?.zone) {
					setFieldValue('zone', result.zone);
				}
				if (result?.city) {
					setFieldValue('city', result.city);
				}
				if (result?.state) {
					setFieldValue('state', result.state);
				}
			} catch (error) {
				// Error already handled by searchPincodeV2
			}
		}
	};

	// Handle address change from Google Maps
	const handleAddressChange = (data, setFieldValue) => {
		// Truncate address to city (safeguard - AddressPickerV2 already truncates, but ensure consistency)
		const truncatedAddress = truncateAddressToCity(data.address, data.city || '');
		setFieldValue('project_address', truncatedAddress);
		setFieldValue('latitude', data.latitude);
		setFieldValue('longitude', data.longitude);
		if (data.state) {
			setFieldValue('state', data.state);
		}
		if (data.pincode) {
			setFieldValue('pincode', data.pincode);
			// Auto-fetch zone and city if pincode is valid
			if (data.pincode.length === 6 && /^\d{6}$/.test(data.pincode)) {
				handlePincodeLookup(data.pincode, setFieldValue);
			}
		}
	};

	// Handle GST number change with validation
	const handleGstNumberChange = (value, setFieldValue) => {
		setFieldValue('gst_number', value);
		if (selectedClientPan && value.length >= 12) {
			const gstPan = value.substring(2, 12);
			if (gstPan !== selectedClientPan) {
				setGstValidationError('GST number must match client PAN number');
			} else {
				setGstValidationError('');
			}
		}
	};

	const handleSubmit = async (values, { setSubmitting }) => {
		// Validation: In create mode, project must be saved before navigating to next step
		if (
			!isEditMode &&
			!isViewMode &&
			!savedProjectId &&
			!isWarehousingProject(values.project_type)
		) {
			message.error('You need to click the save button');
			setSubmitting(false);
			return;
		}

		setSubmitting(true);
		try {
			// Store form data for step 2
			setProjectInfoData(values);

			// Proceed to next step (Node Information)
			// In edit mode, we don't call onSubmit here - that should only happen from step 2
			// when the user clicks "Save Changes" or "Submit"
			setCurrentStep(1);
		} catch (error) {
			// Error handled by parent component
		} finally {
			setSubmitting(false);
		}
	};

	// Handle Save button click
	const handleSaveClick = () => {
		if (!window.projectInfoFormik) return;

		window.projectInfoFormik.setSubmitting(true);
		window.projectInfoFormik.validateForm().then((errors) => {
			if (Object.keys(errors).length === 0) {
				const { values: formValues, setErrors: setFormErrors } = window.projectInfoFormik;
				onSave(formValues, setFormErrors);
			} else {
				// Mark all error fields as touched so Formik renders the error messages
				const touchedFields = Object.keys(errors).reduce((acc, key) => {
					acc[key] = true;
					return acc;
				}, {});
				window.projectInfoFormik.setTouched(touchedFields);
				window.projectInfoFormik.setErrors(errors);
				window.projectInfoFormik.setSubmitting(false);
			}
		});
	};

	// Render navigation buttons
	const renderNavigationButtons = () => {
		if (isViewMode) {
			return (
				<NextButton type="button" onClick={() => setCurrentStep(1)}>
					Next
				</NextButton>
			);
		}

		// Both create and edit mode should have Save and Next buttons
		return (
			<>
				{onSave && !deferCreateToStep2 && (
					<SaveButton
						type="button"
						loading={loading}
						disabled={loading}
						onClick={handleSaveClick}
					>
						Save
					</SaveButton>
				)}
				<NextButton
					type="button"
					onClick={() => {
						// Validation: In create mode, project must be saved before navigating
						if (!deferCreateToStep2 && !isEditMode && !savedProjectId) {
							message.error('You need to click the save button');
							return;
						}
						if (window.projectInfoFormik) {
							window.projectInfoFormik.submitForm();
						}
					}}
				>
					Next
				</NextButton>
			</>
		);
	};

	return (
		<>
			<FormContainer>
				<Formik
					initialValues={initialValues}
					validationSchema={ProjectInfoSchema}
					onSubmit={handleSubmit}
					enableReinitialize
					innerRef={(formikRef) => {
						if (formikRef) {
							window.projectInfoFormik = formikRef;
						}
					}}
				>
					{({ setFieldValue, values }) => (
						<Form>
							{/* Row 1: Project Name */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput label="Project Name*" name="project_name">
										<CustomInput
											name="project_name"
											placeholder="Eg : Intelligent Retail...."
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
								<Col span={12}>
									<FormInput label="Select Client*" name="client_id">
										<Select
											name="client_id"
											placeholder="Select"
											options={clientsData}
											style={{ width: '100%' }}
											loading={loadingClients}
											showSearch
											filterOption={(input, option) =>
												(option?.label ?? '')
													.toLowerCase()
													.includes(input.toLowerCase())
											}
											onChange={(value) => {
												setFieldValue('client_id', value);
												handleClientChange(
													value,
													setFieldValue,
													values.gst_number
												);
												// Clear GST when client changes
												setFieldValue('gst_number', '');
												setGstValidationError('');
											}}
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Row 2: Select Client | Project Address */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput label="Project Address*" name="project_address">
										<AddressPickerV2
											value={values.project_address || ''}
											latitude={values.latitude || ''}
											longitude={values.longitude || ''}
											onChange={(data) =>
												handleAddressChange(data, setFieldValue)
											}
											disabled={isViewMode}
											placeholder="Eg : Intelligent Retail...."
										/>
									</FormInput>
								</Col>
								<Col span={12}>
									<FormInput label="Latitude*" name="latitude">
										<CustomInput
											name="latitude"
											disabled={isViewMode}
											placeholder="Auto-filled from address"
											readOnly
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Row 4: Latitude and Longitude - auto-populated by AddressPicker */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput label="Longitude*" name="longitude">
										<CustomInput
											name="longitude"
											disabled={isViewMode}
											placeholder="Auto-filled from address"
											readOnly
										/>
									</FormInput>
								</Col>
								<Col span={12}></Col>
							</Row>

							{/* Row 5: Pincode | Zone | City | State */}
							<Row gutter={24}>
								<Col span={6}>
									<FormInput label="Pincode*" name="pincode">
										<CustomInput
											name="pincode"
											placeholder="Eg : 517408"
											onChange={(e) => {
												setFieldValue('pincode', e.target.value);
												handlePincodeLookup(e.target.value, setFieldValue);
											}}
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
								<Col span={6}>
									<FormInput label="Zone" name="zone">
										<DisabledInput
											name="zone"
											placeholder={values.zone || 'South'}
											disabled
										/>
									</FormInput>
								</Col>
								<Col span={6}>
									<FormInput label="City" name="city">
										<DisabledInput
											name="city"
											placeholder={values.city || 'South'}
											disabled
										/>
									</FormInput>
								</Col>
								<Col span={6}>
									<FormInput label="State" name="state">
										<DisabledInput
											name="state"
											placeholder={values.state || 'South'}
											disabled
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Row 6: Business Unit | GST Number */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput label="Business Unit*" name="business_unit_id">
										<Select
											name="business_unit_id"
											placeholder="Select"
											options={businessUnitsData}
											style={{ width: '100%' }}
											loading={loadingBusinessUnits}
											showSearch
											filterOption={(input, option) =>
												(option?.label ?? '')
													.toLowerCase()
													.includes(input.toLowerCase())
											}
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
								<Col span={12}>
									<FormInput label="GST Number" name="gst_number">
										<CustomInput
											name="gst_number"
											placeholder="Eg : RPLSJ72927190"
											onChange={(e) =>
												handleGstNumberChange(e.target.value, setFieldValue)
											}
											disabled={isViewMode}
										/>
										{gstValidationError && (
											<div
												style={{
													color: '#ff4d4f',
													fontSize: '12px',
													marginTop: '4px',
												}}
											>
												{gstValidationError}
											</div>
										)}
									</FormInput>
								</Col>
							</Row>

							{/* Row 7: POD Required */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput
										label={
											isWarehousingProject(values.project_type)
												? 'POD Required ?'
												: 'POD Required ?*'
										}
										name="pod_required"
									>
										<Select
											name="pod_required"
											placeholder="Select"
											options={POD_REQUIRED_OPTIONS}
											style={{ width: '100%' }}
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput
										name="adhoc_approval_required"
										label={
											isWarehousingProject(values.project_type)
												? 'Approval of Adhoc Trip Needed?'
												: 'Approval of Adhoc Trip Needed? *'
										}
									>
										<Select
											name="adhoc_approval_required"
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											placeholder="Select"
											options={apporvalTypesOptions}
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Row 8: Project Type */}
							<Row gutter={24}>
								<Col span={24}>
									<ProjectTypePanel>
										<ProjectTypeCopy>
											<ProjectTypeLabel>Project Type</ProjectTypeLabel>
											{PROJECT_TYPE_DESCRIPTIONS[values.project_type] && (
												<ProjectTypeHint>
													{PROJECT_TYPE_DESCRIPTIONS[values.project_type]}
												</ProjectTypeHint>
											)}
										</ProjectTypeCopy>
										<ProjectTypeControl>
											<FormInput name="project_type">
												<Tooltip
													title={
														canEditProjectType
															? ''
															: 'Project type cannot be changed after the project is created.'
													}
												>
													<Select
														name="project_type"
														placeholder="Select"
														options={PROJECT_TYPE_OPTIONS}
														style={{ width: '100%' }}
														disabled={isViewMode || !canEditProjectType}
														onChange={(value) => {
															setFieldValue('project_type', value);
															setProjectType(value);
															// Lets the parent relabel step 2 straight away
															onProjectTypeChange(value);
														}}
													/>
												</Tooltip>
											</FormInput>
										</ProjectTypeControl>
									</ProjectTypePanel>
								</Col>
							</Row>
						</Form>
					)}
				</Formik>
			</FormContainer>

			{/* Navigation Buttons - Outside the white box */}
			<Row justify="end" style={{ marginTop: 24, gap: 16 }}>
				{isEditMode && !isViewMode && (
					<CancelButton onClick={onCancel}>Cancel</CancelButton>
				)}
				{renderNavigationButtons()}
			</Row>
		</>
	);
}

ProjectInformationFormV2.propTypes = {
	projectInfoData: PropTypes.object,
	setProjectInfoData: PropTypes.func.isRequired,
	setCurrentStep: PropTypes.func.isRequired,
	isEditMode: PropTypes.bool,
	isViewMode: PropTypes.bool,
	onSubmit: PropTypes.func,
	onCancel: PropTypes.func,
	onSave: PropTypes.func,
	loading: PropTypes.bool,
	savedProjectId: PropTypes.number,
	onProjectTypeChange: PropTypes.func,
};

ProjectInformationFormV2.defaultProps = {
	projectInfoData: {},
	isEditMode: false,
	isViewMode: false,
	onCancel: () => {},
	onSave: null,
	loading: false,
	savedProjectId: null,
	onProjectTypeChange: () => {},
};
